/* eslint-disable react/prop-types */
import { useRef, useState } from "react";
import { ImagePlus, Loader2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "../ui/sonner";
import { uploadFile, uploadUrl } from "../../lib/uploads";

const btn = (on) => `px-2 py-1 rounded border text-[11px] ${on ? "border-cyan-400 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-[#a0aabf] hover:border-cyan-400/40"}`;

export const gridCols = (sec) => sec.columns || Math.min(3, Math.max(1, (sec.images || []).length));

/** Read view + edit preview of an image grid — same column/width maths as the PDF table. */
export function ImageGridView({ sec, testId }) {
  const imgs = (sec.images || []).filter((i) => i.upload_id);
  if (!imgs.length) return null;
  const w = Math.max(10, Math.min(100, Number(sec.width_pct || 100)));
  const margin = sec.align === "left" ? "12px 0" : sec.align === "right" ? "12px 0 12px auto" : "12px auto";
  return (
    <div style={{ width: `${w}%`, margin, display: "grid", gridTemplateColumns: `repeat(${gridCols(sec)}, minmax(0, 1fr))`, gap: "6px" }} data-testid={testId}>
      {imgs.map((im) => (
        <figure key={im.id} style={{ margin: 0 }}>
          <img src={uploadUrl(im.upload_id)} alt={im.caption || ""} style={{ width: "100%", height: "auto", borderRadius: 4 }} />
          {im.caption && <figcaption className="text-[10px] text-[#a0aabf] italic mt-1 text-center">{im.caption}</figcaption>}
        </figure>
      ))}
    </div>
  );
}

export function ImageGridEditor({ sec, onPatch }) {
  const [busy, setBusy] = useState(false);
  const [wDrag, setWDrag] = useState(null);
  const fileRef = useRef(null);
  const imgs = sec.images || [];
  const w = Number(sec.width_pct || 100);
  const setImgs = (next) => onPatch({ images: next });
  const add = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const added = [];
      for (const f of Array.from(files)) {
        const up = await uploadFile(f, { target_kind: "workbench_report", label: f.name });
        added.push({ id: `img-${Math.random().toString(36).slice(2, 10)}`, upload_id: up.id, caption: "" });
      }
      setImgs([...imgs, ...added]);
    } catch { toast.error("Image upload failed."); } finally { setBusy(false); }
  };
  const move = (i, d) => {
    const n = [...imgs];
    const j = i + d;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j], n[i]];
    setImgs(n);
  };
  return (
    <div className="space-y-3" data-testid={`workbench-grid-editor-${sec.id}`}>
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(4, imgs.length + 1)}, minmax(0, 1fr))` }}>
        {imgs.map((im, i) => (
          <div key={im.id} className="rounded border border-white/10 p-1.5 space-y-1 bg-black/20" data-testid={`workbench-grid-slot-${im.id}`}>
            <div className="relative">
              <img src={uploadUrl(im.upload_id)} alt="" className="w-full h-auto rounded" />
              <div className="absolute top-1 right-1 flex gap-0.5">
                <button type="button" onClick={() => move(i, -1)} className="bg-black/60 rounded p-0.5 text-white" data-testid={`workbench-grid-left-${im.id}`}><ChevronLeft className="w-3 h-3" /></button>
                <button type="button" onClick={() => move(i, 1)} className="bg-black/60 rounded p-0.5 text-white" data-testid={`workbench-grid-right-${im.id}`}><ChevronRight className="w-3 h-3" /></button>
                <button type="button" onClick={() => setImgs(imgs.filter((x) => x.id !== im.id))} className="bg-black/60 rounded p-0.5 text-rose-300" data-testid={`workbench-grid-remove-${im.id}`}><X className="w-3 h-3" /></button>
              </div>
            </div>
            <input
              defaultValue={im.caption || ""}
              onBlur={(e) => e.target.value !== (im.caption || "") && setImgs(imgs.map((x) => (x.id === im.id ? { ...x, caption: e.target.value } : x)))}
              placeholder="Caption (optional)"
              className="w-full bg-black/25 rounded px-1.5 py-1 text-[11px] text-[#cbd5e1] outline-none"
              data-testid={`workbench-grid-caption-${im.id}`}
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="min-h-[90px] rounded border border-dashed border-cyan-400/40 text-cyan-300 flex flex-col items-center justify-center gap-1 text-[11px] hover:bg-cyan-500/5"
          data-testid={`workbench-grid-add-${sec.id}`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          {busy ? "Uploading…" : "Add image"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid={`workbench-grid-file-${sec.id}`} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#647087]">
        Per row
        {[null, 1, 2, 3, 4].map((c) => (
          <button key={String(c)} type="button" className={btn((sec.columns || null) === c)} onClick={() => onPatch({ columns: c })} data-testid={`workbench-grid-cols-${c || "auto"}-${sec.id}`}>
            {c || "Auto"}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#647087]">
        Width · {w}%
        {[25, 50, 75, 100].map((p) => (
          <button key={p} type="button" className={btn(w === p)} onClick={() => onPatch({ width_pct: p })} data-testid={`workbench-grid-width-${p}-${sec.id}`}>{p}%</button>
        ))}
        <input type="range" min={10} max={100} step={5} value={wDrag ?? w} onChange={(e) => setWDrag(Number(e.target.value))} onPointerUp={() => { if (wDrag != null) { onPatch({ width_pct: wDrag }); setWDrag(null); } }} onKeyUp={() => { if (wDrag != null) { onPatch({ width_pct: wDrag }); setWDrag(null); } }} className="flex-1 min-w-[100px] accent-cyan-400" data-testid={`workbench-grid-width-slider-${sec.id}`} />
      </div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#647087]">
        Align
        {["left", "center", "right"].map((a) => (
          <button key={a} type="button" className={btn((sec.align || "center") === a)} onClick={() => onPatch({ align: a })} data-testid={`workbench-grid-align-${a}-${sec.id}`}>{a}</button>
        ))}
      </div>
      <div className="text-[10px] text-[#647087]">Preview (as printed):</div>
      <div className="rounded bg-white/5 p-2"><ImageGridView sec={sec} testId={`workbench-grid-preview-${sec.id}`} /></div>
    </div>
  );
}
